/* eslint-disable no-console */
import {
  UntypedFormArray,
  UntypedFormGroup,
  type AbstractControl,
} from '@angular/forms';
import { type ConditionalKeys } from 'type-fest';

export function markAsTouched(form: UntypedFormGroup | UntypedFormArray): void {
  if (form.invalid) {
    for (const key of Object.keys(form.controls)) {
      const control = form.get(key);

      if (control.invalid) {
        if (
          control instanceof UntypedFormGroup ||
          control instanceof UntypedFormArray
        ) {
          markAsTouched(control);
        } else {
          control.markAsTouched();
          control.updateValueAndValidity();
        }
      }
    }
  }
}

/**
 * Useful to debug a complex formGroup
 *
 * @param formGroup the form to debug
 * @param controlKey could be 'value', 'valid', 'invalid', 'dirty', 'pristine', 'touched', 'untouched', 'pending', 'disabled', 'enabled'
 */
export function debugFormGroup(
  formGroup: UntypedFormGroup,
  controlKey: ConditionalKeys<AbstractControl, boolean> = 'pristine',
): void {
  _debugFormGroup(formGroup, controlKey);
}

/**
 * @internal
 */
function _debugFormGroup(
  formGroup: UntypedFormGroup,
  controlKey: ConditionalKeys<AbstractControl, boolean> = 'pristine',
  isMain: boolean = true,
): void {
  if (isMain) {
    console.groupCollapsed('formGroup: ', formGroup[controlKey]);
  }

  const { controls } = formGroup;

  for (const key in controls) {
    const control = controls[key];

    if (control instanceof UntypedFormArray && control.controls.length) {
      console.group(key + ':', control[controlKey]);

      for (let i = 0; i < control.controls.length; i++) {
        console.group(i);
        _debugFormGroup(
          control.controls[i] as UntypedFormGroup,
          controlKey,
          false,
        );
        console.groupEnd();
      }

      console.groupEnd();
    } else {
      console.log(key + ':', control[controlKey]);
    }
  }

  if (isMain) {
    console.groupEnd();
  }
}

export function syncPristineFromChildren(form: UntypedFormGroup): void {
  const hasDirtyControl = Object.values(form.controls).some(
    control => control.dirty,
  );

  if (hasDirtyControl) {
    form.markAsDirty({ onlySelf: true, emitEvent: false });

    return;
  }

  form.markAsPristine({ onlySelf: true, emitEvent: false });
}
